<template>
  <div class="container v-content-container">
    <div class="row">
      <div class="col-xs-12">
        <h1>
          <i class="fa fa-fw fa-lg fa-male"></i>
          <span v-if="selectedUserId != null">
            {{
              noDataExist ? "User" : `${originalLastName} ${originalFirstName}`
            }}
          </span>
          <span v-else-if="!loading">
            {{ $label("user.new_user", "New user") }}
          </span>
        </h1>
      </div>
    </div>

    <div v-show="loading" class="row">
      <div class="col-xs-12">
        <view-loader
          margin-top-pixels="15"
          border-color="primary"
        ></view-loader>
      </div>
    </div>

    <div class="row">
      <div class="col-xs-12">&nbsp;</div>
    </div>

    <error404 v-if="noDataExist" />

    <template v-show="!loading" v-else>
      <div class="row">
        <div class="col-sm-6">
          <h2>
            <i class="fa fa-fw fa-lg fa-male"></i>
            {{ $label("user.user", "User") }}
          </h2>
          <p></p>
          <div class="v-form-partial">
            <div class="v-form">
              <fieldset>
                <div class="row">
                  <!-- BEGIN Last name -->
                  <section class="col col-6">
                    <text-input
                      v-model="selectedUser.last_name"
                      id="user-last-name"
                      :label="$label('common.last_name', 'Last name')"
                      :placeholder="$label('common.last_name', 'Last name')"
                      :has-error="hasError('last_name')"
                      :error="getError('last_name')"
                      :is-disabled="updating"
                      @keyup.native.enter="createOrUpdateUser"
                    />
                  </section>
                  <!-- END Last name -->

                  <!-- BEGIN First name -->
                  <section class="col col-6">
                    <text-input
                      v-model="selectedUser.first_name"
                      id="user-first-name"
                      :label="$label('common.first_name', 'First name')"
                      :placeholder="$label('common.first_name', 'First name')"
                      :has-error="hasError('first_name')"
                      :error="getError('first_name')"
                      :is-disabled="updating"
                      @keyup.native.enter="createOrUpdateUser"
                    />
                  </section>
                  <!-- END First name -->
                </div>

                <!-- BEGIN Email -->
                <section>
                  <text-input
                    v-model="selectedUser.email"
                    id="user-email"
                    :label="$label('common.email', 'Email')"
                    :placeholder="$label('common.email', 'Email')"
                    :has-error="hasError('email')"
                    :error="getError('email')"
                    :is-disabled="updating"
                    @keyup.native.enter="createOrUpdateUser"
                  />
                </section>
                <!-- END Email -->

                <!-- BEGIN Password -->
                <section v-if="selectedUserId == null">
                  <password-input
                    v-model="selectedUser.password"
                    id="user-password"
                    :label="$label('common.password', 'Password')"
                    :placeholder="$label('common.password', 'Password')"
                    :has-error="hasError('password')"
                    :error="getError('password')"
                    :is-disabled="updating"
                    @keyup.native.enter="createOrUpdateUser"
                  />
                </section>
                <!-- END Password -->

                <!-- BEGIN New Password -->
                <section v-if="selectedUserId != null">
                  <password-input
                    v-model="selectedUser.new_password"
                    id="user-new-password"
                    :label="
                      $label(
                        'common.new_password_optional',
                        'New Password (optional)'
                      )
                    "
                    :placeholder="$label('common.new_password', 'New password')"
                    :has-error="hasError('new_password')"
                    :error="getError('new_password')"
                    :is-disabled="updating"
                    @keyup.native.enter="createOrUpdateUser"
                  />
                </section>
                <!-- END New Password -->

                <!-- BEGIN New Password Confirmation -->
                <section v-if="selectedUserId != null">
                  <password-input
                    v-model="selectedUser.new_password_confirmation"
                    id="user-new-password-confirmation"
                    :label="
                      $label(
                        'common.confirm_new_password_optional',
                        'Confirm New Password (optional)'
                      )
                    "
                    :placeholder="$label('common.new_password', 'New password')"
                    :has-error="hasError('new_password_confirmation')"
                    :error="getError('new_password_confirmation')"
                    :is-disabled="updating"
                    @keyup.native.enter="createOrUpdateUser"
                  />
                </section>
                <!-- END New Password Confirmation -->
              </fieldset>
            </div>
          </div>
        </div>

        <div class="col-sm-6">
          <h2>
            <i class="fa fa-fw fa-lg fa-wrench"></i>
            {{ $label("common.permissions", "Permissions") }}
          </h2>
          <p></p>
          <div class="v-form-partial">
            <div class="v-form">
              <fieldset>
                <div class="row">
                  <!-- BEGIN Permissions -->
                  <section class="col col-12">
                    <label class="label">{{
                      $label("common.permission", "Permission")
                    }}</label>

                    <label class="select">
                      <select id="user-permissions" :disabled="updating">
                        <!-- display empty role first for non-associates -->
                        <option value="administrator">Administrator</option>
                        <option value="readonly"
                          >Mitarbeiter (nur Lesezugriff)</option
                        >
                        <option value="facility-agent"
                          >Standort-Mitarbeiter</option
                        >
                        <option value="facility-readonly"
                          >Standort-Mitarbeiter (nur Lesezugriff)</option
                        >
                        <option value="facility-admin"
                          >Standort-Administrator</option
                        >
                        <option value="facility-light"
                          >Hallen-/Platzwart</option
                        >
                      </select>
                      <i></i>
                    </label>

                    <p v-if="hasError('permissions')" class="text-danger">
                      {{ getError("permissions") }}
                    </p>
                  </section>
                  <!-- END Permissions -->
                </div>

                <div class="row">
                  <!-- BEGIN Facilities -->
                  <section class="col col-12" id="facility_selection">
                    <label class="label typo__label">{{
                      $label("common.facilities", "Facilities")
                    }}</label>
                    <multiselect
                      v-model="facilityValue"
                      tag-placeholder=""
                      placeholder="Search or add facility"
                      label="name"
                      track-by="id"
                      :options="facilityOptions"
                      class="v-multiselect v-form"
                      :multiple="true"
                      :taggable="false"
                      :show-labels="false"
                      @tag="addFacility"
                    >
                    </multiselect>

                    <p v-if="hasError('facilities')" class="text-danger">
                      {{ getError("facilities") }}
                    </p>
                  </section>
                  <!-- END Facilities -->
                </div>
              </fieldset>
            </div>
          </div>
        </div>
      </div>

      <hr />

      <div class="row">
        <div class="col-xs-12 text-center">
          <back-button
            back-link="UsersOverview"
            :class="{ 'is-disabled': updating }"
            id="back-user-button"
            icon="fa fa-lg fa-fw fa-arrow-circle-o-left"
            :text="$label('common.back', 'Back')"
          >
          </back-button>
          &nbsp;
          <info-button
            v-if="selectedUserId == null"
            :class="{ 'is-disabled': updating }"
            id="create-user-button"
            icon="fa fa-lg fa-fw fa-save"
            :text="
              $label(
                'user.create_and_send_login_data_to_user',
                'Create & send login data to user'
              )
            "
            @click.native="createUser"
          >
          </info-button>
          <info-button
            v-if="selectedUserId != null"
            :class="{ 'is-disabled': updating }"
            id="update-user-button"
            icon="fa fa-lg fa-fw fa-save"
            :text="$label('common.save', 'Save')"
            @click.native="updateUser"
          >
          </info-button>
          &nbsp;
          <danger-button
            v-if="selectedUserId != null"
            :class="{ 'is-disabled': updating }"
            id="delete-user-button"
            icon="fa fa-fw fa-lg fa-trash-o"
            :text="$label('common.delete', 'Delete')"
            @click.native="deleteUser"
          >
          </danger-button>
        </div>
      </div>

      <div class="row mb-4">
        <div class="col-xs-12"></div>
      </div>
    </template>
  </div>
</template>

<script>
import Api from "@/api.js";
import swal from "sweetalert";
import FormErrorsMixin from "@/mixins/FormErrors";
import Multiselect from "vue-multiselect";

import ViewLoader from "@/components/Common/ViewLoader";
import TextInput from "@/components/Common/Inputs/TextInput.vue";
import PasswordInput from "@/components/Common/Inputs/PasswordInput.vue";
import BackButton from "@/components/Common/Buttons/BackButton";
import InfoButton from "@/components/Common/Buttons/InfoButton";
import DangerButton from "@/components/Common/Buttons/DangerButton";
import Error404 from "@/components/Common/Error404";

export default {
  name: "UserDetails.vue",

  mixins: [FormErrorsMixin],

  components: {
    ViewLoader,
    TextInput,
    PasswordInput,
    BackButton,
    InfoButton,
    DangerButton,
    Multiselect,
    Error404
  },

  data() {
    return {
      loading: false,
      updating: false,
      noDataExist: false,
      originalFirstName: null,
      originalLastName: null,
      selectedUser: {
        first_name: null,
        last_name: null,
        email: null,
        password: null,
        new_password: null,
        new_password_confirmation: null
      },
      selectedUserId: null,
      failedRequest: false,
      validatedInputs: [
        "first_name",
        "last_name",
        "email",
        "password",
        "new_password",
        "new_password_confirmation"
      ],
      facilityValue: [
        //
      ],
      facilityOptions: [
        { name: "Mehrzweckhalle", id: 1 },
        { name: "Tennishalle", id: 2 },
        { name: "Kinderspielplatz", id: 3 }
      ]
    };
  },

  beforeRouteEnter(to, from, next) {
    next(vm => {
      vm.selectedUserId = vm.$route.params.user_id;
      if (
        typeof vm.selectedUserId !== "undefined" &&
        vm.selectedUserId !== null
      ) {
        if (vm.$route.query.user_created) {
          swal("Success!", "The user was created.", "success").then(() => {
            window.history.pushState(
              {},
              document.title,
              window.location.pathname
            );
          });
        }
        vm.getUser(() => {
          //
        });
      }
    });
  },

  beforeRouteUpdate(to, from, next) {
    if (
      typeof this.selectedUserId !== "undefined" &&
      this.selectedUserId !== null
    ) {
      this.getUser(() => {
        //
      });
    }
    next();
  },

  mounted() {
    //
  },

  methods: {
    addFacility(facility) {
      this.facilityValue.push(facility);
    },

    createOrUpdateUser() {
      if (this.selectedUserId == null) {
        this.createUser();
      } else {
        this.updateUser();
      }
    },

    createUser() {
      if (this.updating) {
        return;
      }

      this.resetErrors();
      this.updating = true;
      const endpoint = this.$partnerEndpoint("users");

      Api()
        .post(endpoint, {
          password: this.selectedUser.password,
          email: this.selectedUser.email,
          first_name: this.selectedUser.first_name,
          last_name: this.selectedUser.last_name
        })
        .then(response => {
          this.selectedUserId = response.data.data.id;
          this.$router.push({
            name: "UserDetails",
            params: { user_id: this.selectedUserId },
            query: { user_created: true }
          });
        })
        .catch(error => {
          this.storeErrors(error.response.data.errors);
          this.$scrollTo("#user-email", 500);
        })
        .finally(() => {
          this.updating = false;
        });
    },

    getUser() {
      if (this.loading) {
        return;
      }

      this.resetErrors();
      this.loading = true;
      const endpoint = this.$partnerEndpoint("users/" + this.selectedUserId);

      Api()
        .get(endpoint)
        .then(response => {
          this.selectedUser = response.data.data;
          this.originalFirstName = this.selectedUser.first_name;
          this.originalLastName = this.selectedUser.last_name;
        })
        .catch(error => {
          if (error.response.status === 404) {
            this.noDataExist = true;
          }
          console.log(error.response);
        })
        .finally(() => {
          this.loading = false;
        });
    },

    updateUser() {
      if (this.loading || this.updating) {
        return;
      }

      this.resetErrors();
      this.updating = true;
      const endpoint = this.$partnerEndpoint("users/" + this.selectedUserId);

      Api()
        .put(endpoint, {
          first_name: this.selectedUser.first_name,
          last_name: this.selectedUser.last_name,
          email: this.selectedUser.email,
          new_password: this.selectedUser.new_password,
          new_password_confirmation: this.selectedUser.new_password_confirmation
        })
        .then(() => {
          this.getUser(() => {
            //
          });
          swal({
            title: "Success!",
            text: "The user was updated.",
            icon: "success"
          });
        })
        .catch(error => {
          this.storeErrors(error.response.data.errors);
          this.$scrollTo("#user-email", 500);
        })
        .finally(() => {
          this.updating = false;
        });
    },

    deleteUser() {
      if (this.loading || this.updating) {
        return;
      }

      swal({
        title: "Are you sure?",
        text:
          "The user will be deleted and will not be able to use his account",
        icon: "warning",
        dangerMode: true,
        buttons: {
          cancel: {
            visible: true,
            text: "Cancel",
            closeModal: true
          },
          confirm: {
            visible: true,
            text: "Yes, delete it!",
            closeModal: true
          }
        }
      }).then(isConfirm => {
        if (isConfirm) {
          const endpoint = this.$partnerEndpoint(
            "users/" + this.selectedUserId
          );

          Api()
            .delete(endpoint)
            .then(response => {
              console.log(response.status);
              this.$router.push({
                name: "UsersOverview",
                query: { user_deleted: true }
              });
            })
            .catch(error => {
              console.log(error.response);
              if (error.response.status === 403) {
                swal({
                  title: "Missing permission",
                  text: "You are not allowed to delete users.",
                  icon: "error"
                  //dangerMode: true,
                });
              }
              if (error.response.status === 409) {
                swal({
                  title: "Your own account",
                  text: "You cannot delete your own account.",
                  icon: "error"
                  //dangerMode: true,
                });
              }
            });
        } else {
          //
        }
      });
    }
  }
};
</script>

<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>

<style scoped>
.mb-4 {
  margin-bottom: 1.2rem;
}
</style>
